import { useEffect, useMemo } from 'react'

import { useSelector } from 'react-redux'

import {
  useGetArtistFeatureFlagsQuery,
  useUpdateArtistFeatureFlagsMutation,
} from 'src/Redux/A-BFeaturesApi'
import {
  getIsArtistNotFromInvitedLink,
  useGetArtistBalanceHistoryQuery,
} from 'src/Redux/artistBalanceHistoryApi'
import {
  EXPIRED_TIMER_VALUE,
  getCheckoutTimer,
} from 'src/Redux/checkoutCountdown-process'
import {
  getHasPaidCampaign,
  usePaidCampaignsCountQuery,
} from 'src/Redux/metadataApi'
import { useBudgetRange } from 'src/Redux/spotifyBudgetRangeApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { OrderStatus } from 'src/Redux/artistOrderApi'

import { useCustomSearchParams } from './useCustomSearchParams'
import { useOrderPaymentPolling } from './useOrderPaymentPolling'

export const QUICK_CHECKOUT_FEATURE_NAME = 'quick_checkout'
export const EXPIRED_TIMER_FEATURE_VALUE = 4

const RESET_AWAITING_STATUSES = [OrderStatus.Checkout, OrderStatus.PaymentSent]
const RESET_MAX_POLL_TICKS = 30

export const useCheckoutTimerFeatureFlag = ({
  platform,
  campaignId,
}: {
  platform: CampaignPlatformTypeEnum
  campaignId?: number
}): void => {
  const { data: paidCampaignsData } = usePaidCampaignsCountQuery(platform)
  const { isLowReachError } = useBudgetRange()
  const { search } = useCustomSearchParams()
  const countdown = useSelector(getCheckoutTimer)
  const paymentStatus = search.get('paymentStatus')
  const isFirstPaid = getHasPaidCampaign(paidCampaignsData)
  const isSuccessPageVisible = paymentStatus === 'success'
  const { isPaid } = useOrderPaymentPolling({
    platform,
    campaignId,
    enabled: isSuccessPageVisible,
    awaitingStatuses: RESET_AWAITING_STATUSES,
    maxPollTicks: RESET_MAX_POLL_TICKS,
  })

  const { data: featureValue, isSuccess: featuresIsSucceeded } =
    useGetArtistFeatureFlagsQuery(void 0, {
      selectFromResult: (result) => ({
        ...result,
        data: result?.data?.features?.find(
          (item) => item?.name === QUICK_CHECKOUT_FEATURE_NAME,
        )?.variation,
      }),
    })
  const [addFeatureForUser] = useUpdateArtistFeatureFlagsMutation()
  const { isArtistNotFromInvitedLink } = useGetArtistBalanceHistoryQuery(null, {
    selectFromResult: getIsArtistNotFromInvitedLink,
  })

  const isNoFeature = featuresIsSucceeded && !featureValue && featureValue !== 0

  const isFeatureConditions =
    (isNoFeature || featureValue === EXPIRED_TIMER_FEATURE_VALUE) &&
    !isLowReachError

  const isQuickCheckoutFeatureAvailable = useMemo(
    () => isFeatureConditions && isArtistNotFromInvitedLink && !isFirstPaid,
    [isArtistNotFromInvitedLink, isFeatureConditions, isFirstPaid],
  )

  useEffect(() => {
    if (isSuccessPageVisible && isPaid) {
      addFeatureForUser({
        featureName: QUICK_CHECKOUT_FEATURE_NAME,
        variation: 0,
      })
    }
  }, [addFeatureForUser, isSuccessPageVisible, isPaid])

  useEffect(() => {
    if (
      isQuickCheckoutFeatureAvailable &&
      countdown !== null &&
      countdown !== EXPIRED_TIMER_VALUE
    ) {
      addFeatureForUser({
        featureName: QUICK_CHECKOUT_FEATURE_NAME,
        variation: 1,
      })
    }
  }, [addFeatureForUser, isQuickCheckoutFeatureAvailable, countdown])
}
